import React, { useEffect, useRef } from 'react';

// eslint-disable-next-line import/no-extraneous-dependencies
import Lottie, { type AnimationItem } from 'lottie-web';

import shapeMorphAnimation from './assets/shape-morph-animation.json';

// Module-level state — persists the current frame across remounts
// so the morphing animation continues smoothly without restarting.
let savedFrame = 0;
let hasPlayedBefore = false;

const getSafeFrame = (frame: number): number => (Number.isFinite(frame) && frame >= 0 ? frame : 0);

const CONTAINER_STYLE: React.CSSProperties = {
	width: 24,
	height: 24,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	overflow: 'visible',
};

const LOTTIE_WRAPPER_STYLE: React.CSSProperties = {
	width: 16,
	height: 16,
};

type MorphingShapeLoaderProps = {
	testId?: string;
};

export const MorphingShapeLoader = ({ testId }: MorphingShapeLoaderProps): JSX.Element => {
	const containerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const currentRef = containerRef.current;
		if (!currentRef) {
			return;
		}

		const baseAnimationConfig = {
			container: currentRef,
			animationData: shapeMorphAnimation,
			renderer: 'svg' as const,
			loop: true,
		};

		let animationInstance: AnimationItem;

		animationInstance = Lottie.loadAnimation({ ...baseAnimationConfig, autoplay: true });

		if (hasPlayedBefore) {
			// Remount — resume from saved frame, while still autoplaying as a visible fallback.
			animationInstance.goToAndPlay(getSafeFrame(savedFrame), true);
		}

		hasPlayedBefore = true;

		return () => {
			// Save current frame before destroying
			savedFrame = getSafeFrame(animationInstance.currentFrame);
			animationInstance.destroy();
		};
	}, []);

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- static styles for lottie container
		<div style={CONTAINER_STYLE} data-testid={testId}>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- static styles for lottie wrapper */}
			<div style={LOTTIE_WRAPPER_STYLE} ref={containerRef} />
		</div>
	);
};
